import { Controller } from "@hotwired/stimulus";
import { setSubmitBlock } from "../../shared/submit_blocks";
import { moneyString } from "../../shared/money_format";

export default class extends Controller {
  static targets = [
    "customAmount",
    "tipCents",
    "submitButton",
    "customAmountContainer",
    // CAL-420: the redesigned checkout shows the
    // running total in two or three places at once
    // (the sticky footer's CTA, the summary sheet,
    // the desktop summary panel), so these are all
    // multi-target.
    "total",
    "tipRow",
    "tipAmount",
    "tipSummary",
    "deliveryFee",
  ];
  static values = {
    orderTotalCents: Number,
    minimumPaymentCents: Number,
    // What the page's own Total already says, so a
    // tip can be added to it. Zero means the total is
    // not a fixed number the browser may recompute:
    // a tiered delivery has none until it is quoted.
    checkoutTotalCents: Number,
    // Rendered server-side by `number_to_currency`,
    // which uses one unit for every currency; taking
    // it from Ruby keeps the two agreeing.
    currencySymbol: { type: String, default: "$" },
    // Rails' own `number.format` marks for the
    // request's locale. Hardcoding en-US renders
    // "1,234.56" beside server amounts printed
    // "1.234,56" on every EUR/BRL/COP/ARS/CLP/CRC
    // venue: same page, two conventions.
    currencyDelimiter: { type: String, default: "," },
    currencySeparator: { type: String, default: "." },
    // What a total reads when it is not yet knowable:
    // a courier delivery before its quote lands.
    // Server-supplied so Ruby and JS cannot drift on
    // the same glyph.
    pendingTotal: { type: String, default: "-" },
    // The footer's "· $18 tip" suffix, translated
    // server-side with its separator inside the
    // msgid. Never assemble that copy here: the
    // word order is not the same in every locale.
    tipSummaryTemplate: { type: String, default: "" },
  };

  connect() {
    this.restorePickedTip();
    this.updateTotals();
    this.validateMinimumPayment();

    // A courier delivery's fee is not knowable
    // server-side, so `checkoutTotalCents` arrives as
    // 0 and every total renders as the pending glyph.
    // `courier-quote` publishes the fee when a quote
    // lands; this controller stays the single writer
    // of every displayed total, because it recomputes
    // on each tip change and would otherwise clobber
    // whatever the quote controller had written.
    this.onQuotePriced = this.handleQuotePriced.bind(this);
    window.addEventListener("courier-quote:priced", this.onQuotePriced);

    // Listen for payment method changes
    this.element.addEventListener(
      "change",
      this.handlePaymentMethodChange.bind(this),
    );
  }

  disconnect() {
    this.element.removeEventListener(
      "change",
      this.handlePaymentMethodChange.bind(this),
    );
    window.removeEventListener("courier-quote:priced", this.onQuotePriced);
  }

  // `feeCents` null means the quote was refused or
  // the address went away: the totals go back to the
  // pending glyph rather than leaving a stale price
  // on a basket nobody can price.
  //
  // `formattedFee` is the courtesy, `feeCents` is the
  // fact. A landing quote carries the server's own
  // string; a quote restored from the hidden fields
  // after a rejected submit carries only cents,
  // because cents are all a form field round-trips.
  // Formatting it here is not a second opinion: it is
  // the SAME `formatMoney` that writes every total
  // beside it, off the same server-supplied symbol and
  // marks, so the fee row and the total cannot come out
  // in two conventions.
  handleQuotePriced(event) {
    const { feeCents, formattedFee } = event.detail || {};
    const pending = feeCents === null || feeCents === undefined;

    this.checkoutTotalCentsValue = pending
      ? 0
      : this.orderTotalCentsValue + feeCents;

    this.deliveryFeeTargets.forEach((el) => {
      el.textContent = pending
        ? this.pendingTotalValue
        : formattedFee || this.formatMoney(feeCents);
    });

    this.updateTotals();
  }

  handlePaymentMethodChange(event) {
    // Check if the change is from a payment choice radio button
    if (
      event.target.type === "radio" &&
      event.target.name.includes("payment_choice")
    ) {
      this.validateMinimumPayment();
    }
  }

  selectTip(event) {
    const button = event.currentTarget;
    const tipCents = button.dataset.tipAmount;

    // The tiles draw their own selected state from
    // the radio (`has-[:checked]`), so all this has to
    // do is carry the value and reveal the field.
    if (button.dataset.tipType === "custom") {
      this.showCustomAmountInput();
      if (this.hasCustomAmountTarget) {
        this.customAmountTarget.focus();
      }
    } else {
      this.hideCustomAmountInput();
      this.tipCentsTarget.value = tipCents;
      if (this.hasCustomAmountTarget) {
        this.customAmountTarget.value = "";
      }

      this.updateTotals();
      this.validateMinimumPayment();
    }
  }

  // The tile the customer picked is the truth about
  // their tip, and `tip_cents` is only its copy.
  //
  // Applying a discount code re-renders the checkout
  // through a morph (CAL-522). The morph keeps the pick
  // (`shared/customer_morph`), but hands the hidden
  // `tip_cents` back as the server rendered it, 0, and
  // every preset's amount has moved with the discounted
  // total: 15% is now 15% of less. So a reconnect reads
  // the pick again rather than trusting the copy, and a
  // percentage tip follows the total it is a percentage
  // of, exactly as the tile now prints it.
  //
  // Nothing picked means nothing to restore: whatever
  // the server put in `tip_cents` stands.
  restorePickedTip() {
    if (!this.hasTipCentsTarget) return;

    const picked = this.element.querySelector(
      'input[type="radio"][data-tip-amount]:checked, ' +
        'input[type="radio"][data-tip-type="custom"]:checked',
    );
    if (!picked) return;

    if (picked.dataset.tipType === "custom") {
      this.showCustomAmountInput();
      if (!this.hasCustomAmountTarget) return;

      const amount = parseFloat(this.customAmountTarget.value) || 0;
      this.tipCentsTarget.value = Math.round(amount * 100);
      return;
    }

    this.tipCentsTarget.value = picked.dataset.tipAmount;
  }

  updateTipFromCustom(event) {
    const customAmount = parseFloat(event.target.value) || 0;
    const tipCents = Math.round(customAmount * 100);

    this.tipCentsTarget.value = tipCents;

    this.updateTotals();
    this.validateMinimumPayment();
  }

  // Keeps every rendering of the money in step with
  // the tip: the tip line appears once there is one,
  // the CTA and the summary agree on the total, and
  // the footer's "1 item" gains its "· $18 tip" (the
  // phrase is the template's, the "·" is drawn by the
  // footer's CSS, never written here).
  //
  // Two jobs, and only the FIRST of them needs a tip
  // field. This method used to bail at the top on
  // `!hasTipCentsTarget`, which took the total with it:
  // a cash-only courier venue offers no tip, so
  // `TipComponent` and its `tipCents` field never
  // render, so every total and every CTA stayed at the
  // pending glyph with a priced quote on screen. The
  // customer was asked to commit to a cash order (the
  // one rail with no payment page to state the amount
  // again) without ever being shown what they owed.
  updateTotals() {
    const tipCents = this.tipCents();

    // The tip UI only exists where a tip can be given.
    if (this.hasTipCentsTarget) {
      this.updateTipRows(tipCents);
    }

    // Zero means "not knowable yet", not "free": an
    // empty cart cannot reach checkout.
    const total = this.checkoutTotalCentsValue
      ? this.formatMoney(this.checkoutTotalCentsValue + tipCents)
      : this.pendingTotalValue;
    this.totalTargets.forEach((el) => {
      el.textContent = total;
    });
  }

  updateTipRows(tipCents) {
    const tipLabel = this.formatMoney(tipCents);

    this.tipAmountTargets.forEach((el) => {
      el.textContent = tipLabel;
    });

    this.tipRowTargets.forEach((el) => {
      el.classList.toggle("hidden", tipCents === 0);
    });

    const summary = this.tipSummaryTemplateValue.replace("%{amount}", tipLabel);
    this.tipSummaryTargets.forEach((el) => {
      el.textContent = tipCents === 0 ? "" : summary;
      el.classList.toggle("hidden", tipCents === 0);
    });
  }

  // Mirrors `number_to_currency`: unit, group mark
  // and decimal mark all come from the server, so the
  // browser cannot disagree with the amounts Rails
  // rendered beside it.
  //
  // The formatting itself lives in `shared/money_format`
  // since the admin order builder began printing the
  // same amounts on a second surface. Two private copies
  // of this is two renderings of one peso.
  formatMoney(cents) {
    return moneyString(cents, {
      symbol: this.currencySymbolValue,
      delimiter: this.currencyDelimiterValue,
      separator: this.currencySeparatorValue,
    });
  }

  // No field means no tip, which is a real zero, not
  // a reason to stop computing the total.
  tipCents() {
    if (!this.hasTipCentsTarget) return 0;

    return parseInt(this.tipCentsTarget.value) || 0;
  }

  validateMinimumPayment() {
    if (!this.hasSubmitButtonTarget) return;
    if (!this.hasTipCentsTarget) return;

    // Check if "Pay now" is selected
    const payNowRadio = this.element.querySelector(
      'input[type="radio"][value="now"]',
    );
    const isPayNow = payNowRadio && payNowRadio.checked;

    const totalCents = this.orderTotalCentsValue + this.tipCents();

    // Show/hide the minimum payment alert
    const minimumPaymentAlert = document.querySelector(
      "[data-minimum-payment-alert]",
    );
    if (minimumPaymentAlert) {
      // Only enforce minimum if paying now with Stripe
      const belowMinimum =
        isPayNow && totalCents < this.minimumPaymentCentsValue;

      minimumPaymentAlert.classList.toggle("hidden", !belowMinimum);
      // The redesigned CTA is not a DaisyUI `btn`; it
      // paints its own disabled state from the
      // attribute with `disabled:` variants.
      this.submitButtonTargets.forEach((button) => {
        setSubmitBlock(button, "minimumBlocked", belowMinimum);
      });
    }
  }

  showCustomAmountInput() {
    if (this.hasCustomAmountContainerTarget) {
      this.customAmountContainerTarget.classList.remove("hidden");
    }
  }

  hideCustomAmountInput() {
    if (this.hasCustomAmountContainerTarget) {
      this.customAmountContainerTarget.classList.add("hidden");
    }
  }
}
